if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.quantityValidationPending = false;

        const quantityInput = this.quantityInput;

        if (quantityInput) {
          quantityInput.addEventListener(
            'input',
            this.onQuantityInput.bind(this)
          );
        }

        this.quantityLimitErrorShown = false;

      }

async onSubmitHandler(evt) {
  evt.preventDefault();

  if (this.quantityValidationPending) {
    return;
  }

  /*
   * If Dawn disabled the button for sold-out/unavailable reasons,
   * keep its normal behavior.
   *
   * If our own quantity-limit code disabled it, we still perform
   * a live cart check because the cart may have changed elsewhere.
   */
  const djLimitDisabled =
    this.submitButton.dataset.djQuantityLimitReached === 'true';

  if (
    this.submitButton.getAttribute('aria-disabled') === 'true' &&
    !djLimitDisabled
  ) {
    return;
  }

  this.quantityValidationPending = true;

  /*
   * Always remove an old Shopify availability error first.
   *
   * This prevents an earlier:
   * "Only 66 items were added..."
   *
   * from remaining visible.
   */
  this.handleErrorMessage();

  let quantityInput = this.quantityInput;
  let totalMax = null;
  let requestedQuantity = 1;
  let quantityWasClamped = false;

  try {
    /*
     * ========================================================
     * DJ RETAIL LIMIT
     * ========================================================
     */

    if (quantityInput) {
      requestedQuantity = Number.parseInt(
        quantityInput.value,
        10
      );

      if (
        Number.isNaN(requestedQuantity) ||
        requestedQuantity < 1
      ) {
        requestedQuantity = 1;
      }

      /*
       * IMPORTANT:
       *
       * data-max = TOTAL allowed quantity, e.g. 50.
       *
       * The normal max="" attribute can be changed by
       * product-info.js to represent only the remaining amount.
       */
      if (quantityInput.dataset.max) {
        totalMax = Number.parseInt(
          quantityInput.dataset.max,
          10
        );

        if (Number.isNaN(totalMax)) {
          totalMax = null;
        }
      }
    }

    /*
     * Only run the custom cart lookup if this product
     * actually has a total maximum.
     *
     * Wholesale/exempt products without data-max skip it.
     */
    if (totalMax !== null) {
      const cartResponse = await fetch(
        `${routes.cart_url}.js`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
          cache: 'no-store',
        }
      );

      if (!cartResponse.ok) {
        throw new Error(
          `Unable to verify cart quantity: ${cartResponse.status}`
        );
      }

      const liveCart = await cartResponse.json();

      const variantId = Number.parseInt(
        this.variantIdInput.value,
        10
      );

      /*
       * Count this variant across every cart line.
       *
       * This also covers cases where Shopify creates separate
       * lines because of properties or selling plans.
       */
      const currentCartQuantity = liveCart.items.reduce(
        (total, item) => {
          if (
            Number(item.variant_id) === variantId
          ) {
            return (
              total +
              Number(item.quantity || 0)
            );
          }

          return total;
        },
        0
      );

      const remainingQuantity = Math.max(
        0,
        totalMax - currentCartQuantity
      );

      /*
       * Synchronize the visible quantity selector immediately.
       */
      if (quantityInput) {
        quantityInput.dataset.cartQuantity =
          currentCartQuantity;

        quantityInput.max =
          remainingQuantity;
      }

      /*
       * ======================================================
       * ALREADY AT MAX
       * ======================================================
       *
       * Absolutely NO /cart/add.js request.
       */
      if (remainingQuantity <= 0) {
        if (quantityInput) {
          quantityInput.value = 0;
        }

        this.submitButton.dataset.djQuantityLimitReached =
          'true';

        this.submitButton.setAttribute(
          'aria-disabled',
          'true'
        );

        this.submitButton.setAttribute(
          'disabled',
          'disabled'
        );

        this.handleErrorMessage(
          `Maximum quantity per cart is ${totalMax}.`
        );

        return;
      }

      /*
       * The cart may have been reduced elsewhere, so clear
       * an old quantity-limit disabled state.
       */
      if (djLimitDisabled) {
        delete this.submitButton.dataset
          .djQuantityLimitReached;

        this.submitButton.removeAttribute(
          'disabled'
        );

        this.submitButton.removeAttribute(
          'aria-disabled'
        );
      }

      /*
       * ======================================================
       * REQUEST IS TOO LARGE
       * ======================================================
       *
       * Example:
       *
       * cart      = 45
       * max       = 50
       * requested = 17
       * allowed   = 5
       *
       * We send 5 to Shopify.
       * Shopify NEVER receives 17.
       */
      if (
        requestedQuantity >
        remainingQuantity
      ) {
        requestedQuantity =
          remainingQuantity;

        quantityWasClamped = true;

        if (quantityInput) {
          quantityInput.value =
            remainingQuantity;
        }

        /*
         * Generic message.
         *
         * No inventory quantity is exposed.
         */
        this.handleErrorMessage(
          `Maximum quantity per cart is ${totalMax}.`
        );
      }
    }

    /*
     * ========================================================
     * CREATE THE REAL SHOPIFY REQUEST
     * ========================================================
     *
     * Do this AFTER the live-cart validation.
     */
    const formData =
      new FormData(this.form);

    /*
     * Explicitly force the validated quantity into FormData.
     *
     * This is the critical protection:
     *
     * even if another piece of DOM is stale, Shopify receives
     * exactly the quantity we calculated above.
     */
    formData.set(
      'quantity',
      String(requestedQuantity)
    );

    if (this.cart) {
      formData.append(
        'sections',
        this.cart
          .getSectionsToRender()
          .map((section) => section.id)
      );

      formData.append(
        'sections_url',
        window.location.pathname
      );

      this.cart.setActiveElement(
        document.activeElement
      );
    }

    this.submitButton.setAttribute(
      'aria-disabled',
      'true'
    );

    this.submitButton.classList.add(
      'loading'
    );

    this.querySelector(
      '.loading__spinner'
    )?.classList.remove('hidden');

    const config =
      fetchConfig('javascript');

    config.headers['X-Requested-With'] =
      'XMLHttpRequest';

    delete config.headers['Content-Type'];

    config.body = formData;

    const fetchResponse = await fetch(
      `${routes.cart_add_url}`,
      config
    );

    const response =
      await fetchResponse.json();

    /*
     * ========================================================
     * SHOPIFY ERROR
     * ========================================================
     */
    if (response.status) {
      publish(
        PUB_SUB_EVENTS.cartError,
        {
          source: 'product-form',
          productVariantId:
            formData.get('id'),

          errors:
            response.errors ||
            response.description,

          message:
            response.message,
        }
      );

      /*
       * IMPORTANT:
       *
       * Never expose Shopify's:
       *
       * "Only 66 items..."
       *
       * for products where we have our custom retail maximum.
       */
      if (totalMax !== null) {
        if (quantityWasClamped) {
          this.handleErrorMessage(
            `Maximum quantity per cart is ${totalMax}.`
          );
        } else {
          /*
           * This could be genuine inventory availability,
           * but we intentionally don't reveal the amount.
           */
          this.handleErrorMessage(
            'The requested quantity is not available.'
          );
        }
      } else {
        /*
         * Wholesale / unrestricted product:
         * preserve Dawn's normal Shopify message.
         */
        this.handleErrorMessage(
          response.description
        );
      }

      this.error = true;

      return;
    }

    /*
     * ========================================================
     * SUCCESS
     * ========================================================
     */

    this.error = false;

    publish(
      PUB_SUB_EVENTS.cartUpdate,
      {
        source: 'product-form',
        productVariantId:
          formData.get('id'),
        cartData: response,
      }
    );

    if (!this.cart) {
      window.location =
        window.routes.cart_url;

      return;
    }

    const quickAddModal =
      this.closest('quick-add-modal');

    if (quickAddModal) {
      document.body.addEventListener(
        'modalClosed',
        () => {
          setTimeout(() => {
            this.cart.renderContents(
              response
            );
          });
        },
        {
          once: true,
        }
      );

      quickAddModal.hide(true);
    } else {
      this.cart.renderContents(
        response
      );
    }
  } catch (error) {
    console.error(
      'Product quantity validation/add error:',
      error
    );

    /*
     * Fail closed.
     *
     * If we cannot verify the custom limit, don't allow an
     * oversized request to leak through to Shopify.
     */
    this.handleErrorMessage(
      'Unable to verify the quantity. Please try again.'
    );

    this.error = true;
  } finally {
    this.quantityValidationPending = false;

    this.submitButton.classList.remove(
      'loading'
    );

    this.querySelector(
      '.loading__spinner'
    )?.classList.add('hidden');

    if (
      this.cart &&
      this.cart.classList.contains(
        'is-empty'
      )
    ) {
      this.cart.classList.remove(
        'is-empty'
      );
    }

    /*
     * Don't re-enable if we've actually reached
     * the custom total maximum.
     */
    if (
      this.submitButton.dataset
        .djQuantityLimitReached !==
      'true'
    ) {
      this.submitButton.removeAttribute(
        'aria-disabled'
      );

      this.submitButton.removeAttribute(
        'disabled'
      );
    }
  }
}

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

 onQuantityInput() {
  if (!this.quantityLimitErrorShown) {
    return;
  }

  this.quantityLimitErrorShown = false;
  this.handleErrorMessage();
}


/*
 * Get the quantity field associated with this product form.
 *
 * Dawn's quantity selector is outside the <product-form> element
 * but connected to the actual form using:
 *
 * form="product-form-..."
 *
 * Because of that, querySelector() on this component is not enough.
 * form.elements includes externally associated form controls.
 */
get quantityInput() {
  if (!this.form) {
    return null;
  }

  const quantity = this.form.elements.namedItem('quantity');

  if (quantity instanceof HTMLInputElement) {
    return quantity;
  }

  return null;
}


get variantIdInput() {
  return this.form.querySelector('[name=id]');
}






    }
  );
}
